import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/components/ui/popover";
import { Star, Clock, Trash2, BookmarkPlus, ChevronDown } from "lucide-react";
import {
  summariseSnapshot,
  type ReviewFilterSnapshot,
  type SavedPreset,
} from "../../utils/filter-presets";

interface ReviewPresetsMenuProps {
  presets: SavedPreset[];
  history: ReviewFilterSnapshot[];
  onSave: (name: string) => void;
  onDelete: (id: string) => void;
  onApply: (snapshot: ReviewFilterSnapshot) => void;
}

const ReviewPresetsMenu = ({
  presets,
  history,
  onSave,
  onDelete,
  onApply,
}: ReviewPresetsMenuProps) => {
  const { t } = useTranslation("mlExports");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName("");
  };

  const apply = (snapshot: ReviewFilterSnapshot) => {
    onApply(snapshot);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
          <Star className="h-3.5 w-3.5" />
          {t("presets.trigger")}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        {/* Save current */}
        <div className="flex items-center gap-1.5 pb-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            placeholder={t("presets.namePlaceholder")}
            className="h-7 text-xs"
          />
          <Button
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={handleSave}
            disabled={!name.trim()}
          >
            <BookmarkPlus className="h-3.5 w-3.5" />
            {t("presets.save")}
          </Button>
        </div>

        {/* Saved presets */}
        {presets.length > 0 && (
          <div className="border-t pt-2">
            <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("presets.savedHeading")}
            </p>
            {presets.map((p) => (
              <div
                key={p.id}
                className="group flex items-center gap-1 rounded px-1 py-1 hover:bg-accent"
              >
                <button
                  className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                  onClick={() => apply(p.snapshot)}
                >
                  <Star className="h-3 w-3 shrink-0 text-amber-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium">
                      {p.name}
                    </span>
                    <span className="block truncate text-[10px] text-muted-foreground">
                      {summariseSnapshot(p.snapshot, t)}
                    </span>
                  </span>
                </button>
                <button
                  className="shrink-0 rounded p-1 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100"
                  onClick={() => onDelete(p.id)}
                  aria-label={t("presets.deletePreset", { name: p.name })}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Recent history */}
        {history.length > 0 && (
          <div className="border-t pt-2">
            <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("presets.recentHeading")}
            </p>
            {history.map((h, i) => (
              <button
                key={i}
                className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-left hover:bg-accent"
                onClick={() => apply(h)}
              >
                <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="truncate text-xs">{summariseSnapshot(h, t)}</span>
              </button>
            ))}
          </div>
        )}

        {presets.length === 0 && history.length === 0 && (
          <p className="px-1 py-2 text-center text-[11px] text-muted-foreground">
            {t("presets.empty")}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ReviewPresetsMenu;
